import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import './styles/TripInsight.scss';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import moment from 'moment';

import DatePicker from 'app/shared/DateTimePicker';
import SearchBox from 'app/shared/SearchBox';
import EmptyState from 'app/shared/EmptyState';
import GroupTree from '../../../../shared/GroupTree/GroupTree';
import { Toaster } from 'app/shared/Toast';
import Tooltip from 'app/shared/Tooltip';
import { VariableSizeList as List } from 'react-window';
import { apiPost } from 'services/api.service';
import { EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2 } from 'services/APIConstants';

import {
	updateTripInsightData,
	fetchTripInsightData,
	fetchTripInsightGraphSummary,
	setTripInsightGroupTree,
	setShowingDataFor,
	clearTripInsightGraphData,
	selectTripInsightData,
	selectTripInsightStatus,
	selectTripInsightGraphData,
} from 'app/config/Redux/global.reducer';

import ChargeEventBucket from './components/ChargeEventBucket';
import Utilization from './components/Utilization';
import { download_csv, getTimerange, JSONSearch, updateSessionMeta } from 'utils/HelperFunctions';
import { getUserInfo } from 'utils/auth';

const timeranges = [
	{ key: '1week', label: '1 Week' },
	{ key: '2week', label: '2 Weeks' },
	{ key: '3week', label: '3 Weeks' },
	{ key: 'month', label: '1 Month' },
];

const insightsMeta = {
	tet: { event: 'Trip', day: 'Daily' },
	cet: { event: 'Charging Event', day: 'Daily' },
};

const statsMap = {
	distance: ['Distance', ' km'],
	socconsumed: ['SoC Consumed', ' %'],
	range: ['Range', ' km'],
	chargeduration: ['Charge Duration', ' min'],
	soccharged: ['SoC Charged', ' %'],
	roc: ['Rate of Charge', ' %'],
};

function TripInsight() {
	const dispatch = useDispatch();
	const location = useLocation();

	// Redux Selectors
	const tripInsight = useSelector(selectTripInsightData);
	const status = useSelector(selectTripInsightStatus);
	const graphData = useSelector(selectTripInsightGraphData);
	const vehicleList = useSelector((state) => state.vehicleList);
	const myGroup = useSelector((state) => state.myGroup);
	const tripInsightGroupTree = useSelector((state) => state.tripInsightGroupTree);
	const showingDataFor = useSelector((state) => state.showingDataFor);

	// Local State
	const [customTime, setCustomTime] = useState(
		moment().subtract(1, 'day').startOf('day').valueOf()
	);
	const [startTime, setStartTime] = useState(moment().subtract(1, 'week').startOf('day').valueOf());
	const [endTime, setEndTime] = useState(moment().subtract(1, 'day').startOf('day').valueOf());
	const [selectedTimerange, setSelectedTimerange] = useState('1week');
	const [isLoading, setLoading] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [modalData, setModalData] = useState([]);
	const [filteredReport, setFilteredReport] = useState([]);
	const [modalMeta, setModalMeta] = useState({});
	const [groups, setGroups] = useState([]);
	const [selectedGroup, setSelectedGroup] = useState(null);
	const [dataForGroup, setDataForGroup] = useState([]);
	const [copiedVehicle, setCopiedVehicle] = useState('');
	const [distanceData, setDistancedata] = useState([]);
	const [socChargedData, setSocChargedData] = useState([]);
	const [chargeDurationData, setChargeDurationData] = useState([]);
	const [rocData, setRocData] = useState([]);
	const [socConsumedData, setSocConsumedData] = useState([]);
	const [rangeData, setRangeData] = useState([]);
	const [isCrashed, setCrashed] = useState(false);
	const [isCustom, setCustome] = useState(true);
	const [modalLoading, setModalLoading] = useState(false);
	useEffect(() => {
		initPreserveState();
		if (!tripInsight) {
			fetchTripInsight();
		}
		window.group = [];

		if (showingDataFor?.['tripinsight']) {
			setDataForGroup(showingDataFor['tripinsight']);
		} else {
			setDataForGroup([window.userGroups ? window.userGroups[0] : '']);
		}
	}, []);

	useEffect(() => {
		if (tripInsightGroupTree.length === 0) {
			let group = myGroup ? { ...myGroup } : null; // Create new object
			if (group && !group.path) {
				if (Object.keys(group).length > 0) {
					group = { ...group.children[0] }; // Create new object
				} else {
					group = {
						path: '/0/0/',
						ppath: '/0/0/',
					};
				}
			}
			window.group = { ...group }; // Create new object
			if (myGroup && Object.keys(myGroup).length > 1) {
				setGroups({ ...group }); // Create new object
				navigate({ ...group }); // Create new object
			}
		} else {
			window.group = { ...tripInsightGroupTree[0] }; // Create new object
			setGroups([...tripInsightGroupTree]); // Create new array
			navigate({ ...tripInsightGroupTree[tripInsightGroupTree.length - 1] }); // Create new object
		}
	}, [myGroup]);

	const isTimerangeOk = (start, end) => {
		let startTime = moment(start);
		let endTime = moment(end);
		let diff = endTime.diff(startTime, 'days');
		if (diff < 0 || diff > 30) {
			return false;
		} else {
			return true;
		}
	};

	const initPreserveState = () => {
		const pageKey = location.pathname.replaceAll('/', '');
		const metaData = JSON.parse(sessionStorage.getItem(pageKey));

		if (metaData) {
			if (metaData.starttime) {
				setStartTime(parseInt(metaData.starttime));
			}
			if (metaData.endtime) {
				setEndTime(parseInt(metaData.endtime));
			}
			if (metaData.range) {
				setSelectedTimerange(metaData.range);
			} else {
				setSelectedTimerange('range');
			}
		}
	};

	const getTimerangeDate = () => {
		let timerange = getTimerange(selectedTimerange, {
			startTime,
			endTime: selectedTimerange === 'range' ? endTime : customTime,
		});
		return timerange;
	};
	const fetchTripInsight = async (rangeBucket = selectedTimerange) => {
		setLoading(true);
		if (!isTimerangeOk(startTime, endTime)) {
			Toaster.error({
				title: 'Invalid Date Selection',
				message: 'Max Date range is 30 days please check your date range',
			});
			return;
		}

		const timerange = getTimerange(rangeBucket, {
			startTime,
			endTime:
				rangeBucket === 'range'
					? endTime < moment().startOf('day').valueOf()
						? moment(endTime).add(1, 'day').startOf('day').valueOf()
						: endTime
					: moment(endTime).add(1, 'day').startOf('day').valueOf(),
		});

		try {
			await dispatch(
				fetchTripInsightData({
					starttime: timerange.starttime,
					endtime: timerange.endtime,
					...(selectedGroup && { groupid: selectedGroup.groupid }),
				})
			).unwrap();

			if (selectedGroup) {
				setDataForGroup(groups);
				dispatch(
					setShowingDataFor({
						...showingDataFor,
						tripinsight: groups,
					})
				);
			}

			updatePreserveState('activeDate', 0);
		} catch (error) {
			Toaster.error({
				title: 'Error',
				message: error.message,
			});
		}
		setLoading(false);
	};

	const handleSummaryInsight = (data, limitType, widgetType, insightType) => {
		let limit = data.data.range;
		let range;
		if (limitType === 'distance' || limitType === 'range') {
			range = limit.split('km');
		} else {
			range = limit.split('%');
		}

		if (limit.includes('-')) {
			limit = limit.split('-');
			setModalMeta({
				tab: limitType,
				lowerLimit: limit[0],
				upperLimit: limit[1],
				type: widgetType,
				insightType: insightType,
			});
			fetchInsightSummary(
				selectedTimerange,
				parseInt(limit[0]),
				parseInt(limit[1]),
				limitType,
				insightType,
				widgetType,
				range[0]
			);
		} else {
			// let range = limit.split('km')
			limit = limit.split('>');
			setModalMeta({
				tab: limitType,
				// lowerLimit: limit[1],
				upperLimit: `>${limit[1]}`,
				type: widgetType,
				insightType: insightType,
			});

			fetchInsightSummary(
				selectedTimerange,
				parseInt(limit[1]),
				1000,
				limitType,
				insightType,
				widgetType,
				range[0]
			);
		}
	};

	const processModalData = (eventList) => {
		const newEventList = eventList.map(([vehicleId, events]) => ({
			vehicleno: findVehicleNo(vehicleId),
			events,
		}));
		setModalData(newEventList);
		setFilteredReport(newEventList);
	};

	const findVehicleNo = (id) => {
		const vehicleInfo = vehicleList.find((vehicle) => vehicle.vehicleid === parseInt(id));
		return vehicleInfo ? vehicleInfo.vehicleno : id;
	};

	const fetchInsightSummary = async (
		rangeBucket = selectedTimerange,
		lowerLimit,
		upperLimit,
		limitType,
		insightType,
		widgetType,
		limit
	) => {
		setModalLoading(true);
		let historyData = {};
		let eventData = [];
		let timerange = getTimerange(rangeBucket, {
			startTime,
			endTime: rangeBucket === 'range' ? endTime : customTime,
		});
		let payload = {
			starttime: timerange.starttime,
			endtime: timerange.endtime,
			groupid: selectedGroup.groupid,
		};
		if (widgetType === 'tet') {
			limit = limit.replaceAll(' ', '');
			if (limitType === 'distance') {
				let response;
				payload.limittype = ['distance'];
				if (distanceData === undefined || !distanceData.length) {
					response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2, payload);
					setDistancedata(response.data);
				}
				if (insightType === 'day') {
					// eventData = props.tripInsight.tet.distance.dist.overall.day
					eventData =
						(response && response.data && response.data[0].distance.day) ||
						distanceData[0].distance.day;
				} else if (insightType === 'event') {
					// eventData = props.tripInsight.tet.distance.dist.overall.event
					eventData =
						(response && response.data && response.data[0].distance.event) ||
						distanceData[0].distance.event;
				}
			} else if (limitType === 'socconsumed') {
				let response;
				payload.limittype = ['socconsumed'];
				if (socConsumedData === undefined || !socConsumedData.length) {
					response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2, payload);
					setSocConsumedData(response.data);
				}
				if (insightType === 'day') {
					// eventData = props.tripInsight.tet.socconsumed.dist.overall.day
					eventData =
						(response && response.data && response.data[0].socconsumed.day) ||
						socConsumedData[0].socconsumed.day;
				} else if (insightType === 'event') {
					// eventData = props.tripInsight.tet.socconsumed.dist.overall.event
					eventData =
						(response && response.data && response.data[0].socconsumed.event) ||
						socConsumedData[0].socconsumed.event;
				}
			} else if (limitType === 'range') {
				let response;
				payload.limittype = ['range'];
				if (rangeData === undefined || !rangeData.length) {
					response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2, payload);
					setRangeData(response.data);
				}
				if (insightType === 'day') {
					// eventData = props.tripInsight.tet.range.dist.overall.day
					eventData =
						(response && response.data && response.data[0].range.day) || rangeData[0].range.day;
				} else if (insightType === 'event') {
					// eventData = props.tripInsight.tet.range.dist.overall.event
					eventData =
						(response && response.data && response.data[0].range.event) || rangeData[0].range.event;
				}
			}
			for (let idx in eventData) {
				if (eventData[idx].range === limit) {
					historyData = eventData[idx].history;
					break;
				}
			}
			processModalData(Object.entries(historyData));
		} else {
			// let timerange = getTimerange(rangeBucket, {
			// 	startTime,
			// 	endTime: rangeBucket === 'range' ? endTime : customTime,
			// });
			// let payload = {
			// 	starttime: timerange.starttime,
			// 	endtime: timerange.endtime,
			// 	lowerlimit: lowerLimit,
			// 	upperlimit: upperLimit,
			// 	limittype: limitType,
			// 	type:insightType
			// };
			// if (selectedGroup) {
			// 	payload.groupid = selectedGroup.groupid;
			// }
			// const response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY, payload);
			// processModalData(Object.entries(response.data));
			limit = limit.replaceAll(' ', '');
			if (limitType === 'soccharged') {
				payload.limittype = ['soccharged'];
				let response;
				if (socChargedData === undefined || !socChargedData.length) {
					response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2, payload);
					setSocChargedData(response.data);
				}

				if (insightType === 'day') {
					// eventData = props.tripInsight.cet.soccharged.dist.overall.day
					eventData =
						(response && response.data && response.data[0] && response.data[0].soccharged.day) ||
						socChargedData[0].soccharged.day;
				} else if (insightType === 'event') {
					// eventData = props.tripInsight.cet.soccharged.dist.overall.event
					eventData =
						(response && response.data && response.data[0] && response.data[0].soccharged.event) ||
						socChargedData[0].soccharged.event;
				}
			} else if (limitType === 'chargeduration') {
				limit = limit.split('min')[0];
				payload.limittype = ['chargeduration'];
				let response;
				if (chargeDurationData === undefined || !chargeDurationData.length) {
					response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2, payload);
					setChargeDurationData(response.data);
				}
				if (insightType === 'day') {
					// eventData = props.tripInsight.cet.chargeduration.dist.overall.day
					eventData =
						(response && response.data && response.data[0].chargeduration.day) ||
						chargeDurationData[0].chargeduration.day;
				} else if (insightType === 'event') {
					// eventData = props.tripInsight.cet.chargeduration.dist.overall.event
					eventData =
						(response && response.data && response.data[0].chargeduration.event) ||
						chargeDurationData[0].chargeduration.event;
				}
			} else if (limitType === 'roc') {
				payload.limittype = ['roc'];
				let response;
				if (rocData === undefined || !rocData.length) {
					response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2, payload);
					setRocData(response.data);
				}
				if (insightType === 'day') {
					// eventData = props.tripInsight.cet.roc.dist.overall.day
					eventData = (response && response.data && response.data[0].roc.day) || rocData[0].roc.day;
				} else if (insightType === 'event') {
					// eventData = props.tripInsight.cet.roc.dist.overall.event
					eventData =
						(response && response.data && response.data[0].roc.event) || rocData[0].roc.event;
				}
			}
			for (let idx in eventData) {
				if (eventData[idx].range === limit) {
					historyData = eventData[idx].history;
					break;
				}
			}
			processModalData(Object.entries(historyData));
		}
		setTimeout(() => {
			setModalLoading(false);
		}, 100);
	};

	const downloadReport = () => {
		Toaster.error({
			title: 'No Data',
			message: "Oops! Seems like you don't have any data to download",
		});
	};

	// eslint-disable-next-line
	const handleEndTime = (time) => {
		setCustomTime(time);
		setSelectedTimerange('day');
	};

	const handleCustomTime = (key) => {
		setSelectedTimerange(key);
		updatePreserveState('range', key);
		setDistancedata([]);
		setChargeDurationData([]);
		setSocChargedData([]);
		setSocConsumedData([]);
		setRangeData([]);
		setRocData([]);
		if (key !== 'range') {
			fetchTripInsight(key);
		}
		let timerange = getTimerange(key, {
			startTime,
			endTime: endTime,
		});
		setStartTime(timerange.starttime);
		setEndTime(moment(timerange.endtime).subtract(1, 'day').startOf('day').valueOf());
		updatePreserveState('starttime', timerange.starttime);
		updatePreserveState(
			'endtime',
			moment(timerange.endtime).subtract(1, 'day').startOf('day').valueOf()
		);
	};

	const updatePreserveState = (key, value) => {
		let pageKey = location.pathname.replaceAll('/', '');
		updateSessionMeta(key, value, pageKey);
	};

	const searchEvents = (key) => {
		let result = JSONSearch(key, modalData, ['vehicleno']);
		setFilteredReport(result);
	};

	const getCSVName = () => {
		let auth = getUserInfo();
		let date = moment().format('DD-MM-YYYY_h:mmA');
		return auth.username + '_Charging_Events_Per_Vehicel_Per_Day_' + date + '.csv';
	};

	const JSONToCSV = () => {
		let reportData = modalData;
		let str = '';
		let heads = [
			{
				hdg: 'Vehicle No',
			},
			{
				hdg: 'Chraging Started At',
			},
			{
				hdg: 'Start SOC (%)',
			},
			{
				hdg: 'Chraging Ended At',
			},
			{
				hdg: 'End SOC (%)',
			},
			{
				hdg:
					modalMeta.tab === 'distance'
						? 'Distance (km)'
						: modalMeta.tab === 'range'
						? 'Range (km)'
						: modalMeta.tab === 'socconsumed'
						? 'SOC Consumed (%)'
						: modalMeta.tab === 'soccharged'
						? 'SOC charged (%)'
						: modalMeta.tab === 'chargeduration'
						? 'Charge Duration'
						: modalMeta.tab === 'roc'
						? 'RoC (%)'
						: '',
			},
		];
		let headline = '';
		for (var i = 0; i < heads.length; i++) {
			if (headline !== '') headline += ',';
			headline += heads[i]['hdg'];
		}
		str += headline + '\r\n';
		for (let idx in reportData) {
			let line = '';
			if (line !== '') line += ',';
			line += reportData[idx].vehicleno;

			str += '\r\n' + line;
			line = '';
			for (let jdx in reportData[idx].events) {
				if (line !== '') line += ',';
				line += ',';
				line +=
					moment(reportData[idx].events[jdx][0].starttime).format('D MMM YYYY h:mm A') +
					',' +
					reportData[idx].events[jdx][0].startsoc +
					',' +
					moment(reportData[idx].events[jdx][0].endtime).format('D MMM YYYY h:mm A') +
					',' +
					reportData[idx].events[jdx][0].endsoc;
				if (modalMeta.tab === 'distance')
					line += ',' + reportData[idx].events[jdx][0].distance || 'NA';
				if (modalMeta.tab === 'range') line += ',' + reportData[idx].events[jdx][0].mileage || 'NA';
				if (modalMeta.tab === 'socconsumed')
					line += ',' + reportData[idx].events[jdx][0].socconsumed || 'NA';
				if (modalMeta.tab === 'soccharged')
					line += ',' + reportData[idx].events[jdx][0].soccharged || 'NA';
				if (modalMeta.tab === 'chargeduration')
					line +=
						',' +
							moment.utc(reportData[idx].events[jdx][0].chargeduration).format('H') +
							' hr ' +
							moment.utc(reportData[idx].events[jdx][0].chargeduration).format('m') +
							' min' || 'NA';
				if (modalMeta.tab === 'roc') line += ',' + reportData[idx].events[jdx][0].roc || 'NA';

				str += line + '\r\n';
				line = '';
			}
			// str+=line+"\r\n";
		}
		// return;
		download_csv(str, getCSVName());
	};

	const getModalMetaRender = (data) => {
		let insighType =
			modalMeta.type === 'tet'
				? ['distance', 'socconsumed', 'range']
				: ['soccharged', 'chargeduration', 'roc'];

		let insightsUI = [];
		for (let idx in insighType) {
			insightsUI.push(
				<div key={idx} className="miniChip">
					<div
						style={{
							border:
								insighType[idx] === modalMeta.tab
									? modalMeta.type === 'tet'
										? '3px solid #EB5971'
										: ' 3px solid #5CE3B5'
									: '',
							fontWeight: insighType[idx] === modalMeta.tab ? 600 : 400,
						}}
					>
						{statsMap[insighType[idx]][0]}
					</div>
					<div
						style={{
							backgroundColor: modalMeta.type === 'tet' ? '#eb5971' : '#61dca2',
							borderColor: modalMeta.type === 'tet' ? '#eb5971' : '#61dca2',
							color: modalMeta.type === 'tet' ? '#ffff' : 'black',
							fontWeight: insighType[idx] === modalMeta.tab ? '700' : '500',
							// fontSize:insighType[idx] ===  modalMeta.tab ? "0.80rem" : '0.64rem'
						}}
					>
						{insighType[idx] === 'chargeduration'
							? moment.utc(data[insighType[idx]]).format('H') +
							  ' hr ' +
							  moment.utc(data[insighType[idx]]).format('m') +
							  ' min'
							: insighType[idx] === 'range'
							? data['mileage']
								? data['mileage']
								: 'NA'
							: data[insighType[idx]]}{' '}
						{insighType[idx] === 'chargeduration'
							? ''
							: insighType[idx] === 'range'
							? data['mileage']
								? statsMap[insighType[idx]][1]
								: ''
							: statsMap[insighType[idx]][1]}
					</div>
				</div>
			);
		}
		return insightsUI;
	};
	const navigate = (group) => {
		const rootGroupPath = '';
		const pathArray = [...group.path.split('/')].slice(1, -1);
		let currentPath = rootGroupPath;
		const nav = [{ ...window.group }]; // Create new object
		let currentNode = nav[0];

		for (const segment of pathArray) {
			currentPath += '/' + segment;
			const child = currentNode.children?.find((child) => child.path === currentPath + '/');
			if (child) {
				const newNode = {
					...child,
					children: child.children ? [...child.children] : [],
				};
				nav.push(newNode);
				currentNode = newNode;
			}
		}

		setGroups(nav);

		if (nav.length > 1) {
			dispatch(setTripInsightGroupTree([...nav]));
		} else if (myGroup?.children?.length) {
			dispatch(setTripInsightGroupTree([{ ...myGroup.children[0] }]));
		}

		setSelectedGroup({ ...group }); // Create new object
	};
	const getSize = (index) => {
		let entries = Object.keys(filteredReport[index].events).length;
		return entries > 1 ? 120 + entries * 50 : 120;
	};
	const copyVehicleNo = (containerid, e) => {
		e.stopPropagation();
		let r = document.createRange();
		r.selectNode(document.getElementById(containerid));
		window.getSelection().removeAllRanges();
		window.getSelection().addRange(r);
		document.execCommand('copy');
		window.getSelection().removeAllRanges();
		setCopiedVehicle(containerid);
	};
	const RenderTable = ({ index, key, style }) => {
		return (
			<div className="EventsDay" style={style}>
				<div className="DayBucketing">
					<Tooltip
						text={
							copiedVehicle === 'myvehicle' + index ? (
								<>
									Copied <i className="fas fa-check-circle"></i>
								</>
							) : (
								'Click to copy vehicle no'
							)
						}
					>
						<div
							onClick={(e) => copyVehicleNo('myvehicle' + index, e)}
							className="date"
							id={'myvehicle' + index}
						>
							{filteredReport[index].vehicleno}
						</div>
					</Tooltip>
				</div>
				{Object.entries(filteredReport[index].events).map((dayEvent, key2) => {
					return (
						<div className="EventBox">
							<div className="EventBoxLeft">
								<span
									style={{
										marginLeft: '0.5rem',
										fontSize: 13,
										fontWeight: 500,
									}}
								>
									{moment(parseInt(dayEvent[0])).format('D MMM YY')}
								</span>
							</div>
							<div className="EventBoxRight">
								{dayEvent[1].map((cdevent, key3) => {
									return (
										<div style={{ display: 'flex' }}>
											{getModalMetaRender(cdevent)}
											<div className="StatsChip">
												<div>
													{moment(cdevent.starttime).format('D MMM h:mm A')} -{' '}
													{moment(cdevent.endtime).format('D MMM h:mm A')}
												</div>
												<div
													style={{
														backgroundColor: modalMeta.type === 'tet' ? '#eb5971' : '#61dca2',
														borderColor: modalMeta.type === 'tet' ? '#eb5971' : '#61dca2',
														color: modalMeta.type === 'tet' ? '#ffff' : 'black',
														fontWeight: 500,
													}}
												>
													{/* {cdevent[0].value} - {cdevent[0].value} */} {cdevent.startsoc + '%'} -{' '}
													{cdevent.endsoc + '%'}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<div className="TripInsight">
			<div className="ControlPanel">
				<div className="LeftControl">
					{/* <ToggleButton selected={isCustom} changeDateType={() => setCustome(!isCustom)} /> */}
					<div className="group-tree">
						{/* <GroupTree
							props={{ groups: groups, selectedGroup: selectedGroup }}
							navigate={navigate}
						/> */}
					</div>
				</div>
				<div className="RightControl">
					{/* <DatePicker
						value={startTime}
						onChange={(date) => setStartTime(date.timestamp)}
						showTime={true}
					/> */}

					{/* <DatePicker
								value={customTime}
								onChange={handleEndTime}
								showTime={false}
								withBackground={selectedTimerange === 'day' ? true : false}
							/> */}
					{timeranges.map((timerange, key) => {
						return (
							<div
								key={key}
								className={
									'TimerangeButton ' +
									(selectedTimerange === timerange.key ? 'activeTimerange' : '')
								}
								onClick={() => handleCustomTime(timerange.key)}
							>
								<span>{timerange.label}</span>
							</div>
						);
					})}
					<DatePicker
						value={startTime}
						onChange={(date) => {
							setSelectedTimerange('range');
							updatePreserveState('starttime', date);
							updatePreserveState('range', null);
							setStartTime(date);
						}}
						disableToday={true}
						showTime={false}
					/>
					<DatePicker
						value={endTime}
						onChange={(date) => {
							setSelectedTimerange('range');
							updatePreserveState('endtime', date);
							updatePreserveState('range', null);
							setEndTime(date);
						}}
						disableToday={true}
						showTime={false}
					/>
					<div
						className={
							'FetchButton ' +
							(isLoading || !isTimerangeOk(startTime, endTime) ? 'disableButton' : '')
						}
						onClick={() => fetchTripInsight('range')}
					>
						OK
					</div>

					{!isTimerangeOk(startTime, endTime) ? (
						<small className="dateMsg">Max date range allowed is 30 days</small>
					) : null}
					{/* {!isCustom ? (
						<div
							className={
								'FetchButton ' +
								(isLoading || !isTimerangeOk(startTime, endTime) ? 'disableButton' : '')
							}
							onClick={() => fetchTripInsight('range')}
						>
							OK
						</div>
					) : null} */}
					{/* <div className="ControlButton">
						<DownloadIcon onClick={downloadReport} style={{ cursor: 'pointer' }} />
					</div> */}
				</div>
			</div>
			{isLoading ? (
				// <RadialProgress isLoading={true} />
				<div className="loader">
					LOADING<span></span>
				</div>
			) : tripInsight !== null ? (
				<>
					<div className="ChargingEventContainer">
						<div className="DataInfoPanel">
							<div className="Title">
								Charging Events <small style={{ fontSize: 8, color: 'gray' }}> / day</small>
							</div>
							<div className="DataInfoCenter">
								<span>Showing for &nbsp;</span>
								{dataForGroup.map((group, key) => {
									if (key < dataForGroup.length - 1) {
										return (
											<div style={{ display: 'flex' }}>
												<span>{` ${group.name}`}</span>
												<div className="hni-icon">
													<span className="fa fa-chevron-right"></span>
												</div>
											</div>
										);
									} else {
										return <span>{` ${group.name}`}</span>;
									}
								})}
							</div>

							<div className="DataInfoRight">
								<span>Vehicles:&nbsp; </span>
								{tripInsight && (
									<span style={{ width: '4rem' }}>{`  ${tripInsight.totalvehicles}`}</span>
								)}
							</div>
						</div>
						<div className="BucketContainers">
							<ChargeEventBucket
								data={tripInsight.chargingeventdist}
								vehicleList={vehicleList}
								openModal={() => setOpenModal(false)}
								timerange={getTimerangeDate()}
							/>
						</div>
					</div>
					<div className="AnalyticsContainer">
						<Utilization
							type={'tet'}
							tripInsight={tripInsight.tet}
							updatePreserveState={(key, value) => updatePreserveState(key, value)}
							path={location.pathname}
							fetchInsightSummary={(data, type, insightType) => {
								setOpenModal(true);
								handleSummaryInsight(data, type, 'tet', insightType);
							}}
							timerange={getTimerangeDate()}
						/>

						<Utilization
							type={'cet'}
							tripInsight={tripInsight.cet}
							updatePreserveState={(key, value) => updatePreserveState(key, value)}
							path={location.pathname}
							fetchInsightSummary={(data, type, insightType) => {
								setOpenModal(true);
								handleSummaryInsight(data, type, 'cet', insightType);
							}}
							timerange={getTimerangeDate()}
						/>

						<div className="Modal" style={{ display: openModal ? 'block' : 'none' }}>
							<div className="ModalContent">
								<div className="ModalHeader">
									<div className="HeaderLeft">
										<span>
											{moment(getTimerangeDate().starttime).format('MMM DD')} -{' '}
											{moment(getTimerangeDate().endtime).format('MMM DD')}
										</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center' }}>
										<div className="SearchControl">
											<SearchBox placeholder={'Search'} rounded onSearch={searchEvents} />
										</div>
										<DownloadIcon
											onClick={JSONToCSV}
											style={{ cursor: 'pointer', marginRight: 14 }}
										/>

										<span onClick={() => setOpenModal(false)} className="close">
											&times;
										</span>
									</div>
								</div>
								<div className="ModalHeading">
									<div className="heading">
										<div>
											Vehicles with{' '}
											{modalMeta.type === 'cet'
												? (statsMap[modalMeta.tab]
														? statsMap[modalMeta.tab][0]
														: statsMap[modalMeta.tab]) +
												  ' / ' +
												  insightsMeta[modalMeta.type]?.[modalMeta.insightType] +
												  (modalMeta.lowerLimit
														? ' [ ' + modalMeta.lowerLimit + '-' + modalMeta.upperLimit + ']'
														: ' [ ' + modalMeta.upperLimit + ']')
												: insightsMeta[modalMeta.type]?.[modalMeta.insightType] +
												  ' ' +
												  (statsMap[modalMeta.tab]
														? statsMap[modalMeta.tab][0]
														: statsMap[modalMeta.tab]) +
												  (modalMeta.lowerLimit
														? ' [ ' + modalMeta.lowerLimit + '-' + modalMeta.upperLimit + ']'
														: ' [ ' + modalMeta.upperLimit + ']')}
										</div>{' '}
										{filteredReport.length ? (
											<div> Vehicle count: {filteredReport.length} </div>
										) : null}
									</div>
								</div>
								<div className="ModalContainer">
									<div className="WidgetView">
										{modalLoading ? (
											<div className="loader2">
												LOADING<span></span>
											</div>
										) : (
											<div className="EventsView">
												<List
													className="EventsViewContainer"
													height={600}
													itemCount={filteredReport.length}
													itemSize={getSize}
													width={'100%'}
												>
													{RenderTable}
												</List>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			) : (
				<EmptyState
					title={isCrashed ? 'Something went wrong' : 'No Data'}
					description={
						isCrashed
							? "Looks like we couldn't fetch data for given timerange"
							: 'Looks like there is no data for selected date range'
					}
				/>
			)}
		</div>
	);
}

export default TripInsight;
